import { api, errorMessage } from "@/lib/api";
import { isAxiosError, type Method } from "axios";

// E1-F contract adapter. Keep all equipment wire types and HTTP calls here.
export type EquipmentGroup =
  "skela" | "oplata" | "podupiraci" | "alat" | "ostalo";
export type EquipmentCondition =
  | "ispravno"
  | "osteceno"
  | "u_radioni"
  | "u_prijevozu"
  | "otpisano"
  | "nestalo"
  | "vraceno_najmodavcu";
export interface EquipmentArticle {
  id: string;
  naziv: string;
  skupina: EquipmentGroup;
  nacin: "komad" | "kolicina";
  jedinica: string;
  m2_po_kom: string | null;
  nabavna_cijena: string | null;
  aktivna: boolean;
}
export interface EquipmentPlace {
  id: string;
  naziv: string;
  vrsta: "baza" | "gradiliste" | "radiona";
  gradiliste_id: string | null;
  dobavljac_id?: string | null;
  aktivno: boolean;
  popisano_at: string | null;
  inventura_istekla: boolean;
}
export interface EquipmentOwnership {
  najam_id: string | null;
  najmodavac: string | null;
  povrat_do: string | null;
}
export interface EquipmentBalance extends EquipmentOwnership {
  ceka_otpis?: number;
  artikl_id: string;
  mjesto_id: string;
  ispravno: number;
  osteceno: number;
  u_radioni: number;
  u_prijevozu: number;
}
export interface EquipmentPiece extends EquipmentOwnership {
  ceka_otpis?: boolean;
  id: string;
  artikl_id: string;
  mjesto_id: string;
  inventarni_broj: string;
  serijski_broj: string | null;
  stanje: EquipmentCondition;
  qr_token: string;
  napomena: string | null;
  rok_povratka: string | null;
  fotka: string | null;
}
export interface EquipmentTransferItem {
  id?: string;
  artikl_id: string;
  komad_id: string | null;
  najam_id: string | null;
  poslano: number;
  primljeno: number | null;
  razlika_razlog: string | null;
  razlika_rjesenje?: "pronadeno" | "nestalo" | null;
}
export interface EquipmentTransfer {
  id: string;
  iz_mjesta_id: string;
  u_mjesto_id: string;
  poslao: string;
  primio?: string | null;
  poslano_at: string;
  status: "u_prijevozu" | "primljeno" | "primljeno_s_razlikom" | "ponisteno";
  stavke: EquipmentTransferItem[];
  dokument?: EquipmentDocument;
}
export interface EquipmentDocument {
  id: string;
  broj: string;
  kretanje_id: string;
  content_url?: string;
}
export interface EquipmentEvidenceInput {
  bez_fotke_razlog?: string;
  bez_potpisa_razlog?: string;
}
export interface EquipmentEvent {
  user_id: string;
  id: string;
  vrsta: string;
  artikl_id: string;
  komad_id: string | null;
  kolicina: number;
  iz_mjesta_id: string | null;
  u_mjesto_id: string | null;
  najam_id: string | null;
  razlog: string | null;
  vrijeme: string;
  datum: string;
  korisnik: string;
  fotke: { id: string; naziv: string; url: string }[];
  status: "provedeno" | "ceka_potvrdu" | "odbijeno" | "ponisteno";
  izvrseno: boolean;
  odobrava_id: string | null;
  izvor_kretanje_id?: string | null;
  ponistava_id?: string | null;
  dokument?: EquipmentDocument;
  bez_potpisa_razlog?: string | null;
  bez_fotke_razlog?: string | null;
}
export interface EquipmentOverview {
  stize?: EquipmentTransfer[];
  artikli: EquipmentArticle[];
  mjesta: EquipmentPlace[];
  stanje: EquipmentBalance[];
  komadi: EquipmentPiece[];
  plocice: {
    u_prijevozu: number;
    ceka_potvrdu: number;
    radiona_preko_roka: number;
    nije_popisano: number;
    inventura_istekla: number;
    bez_potpisa?: number;
  };
}
export interface EquipmentShortage {
  selidba_id: string;
  stavka_id: string;
  iz_mjesta_id: string;
  u_mjesto_id: string;
  odrediste_naziv: string;
  artikl_id: string;
  komad_id: string | null;
  najam_id: string | null;
  kolicina: number;
  ceka_upravu: boolean;
}
export interface EquipmentPlaceDetail {
  mjesto: EquipmentPlace;
  artikli: EquipmentArticle[];
  stanje: EquipmentBalance[];
  komadi: EquipmentPiece[];
  stize: EquipmentTransfer[];
  trazi_se?: EquipmentShortage[];
  upozorenja: string[];
  povijest?: EquipmentEvent[];
}
export interface EquipmentPieceDetail {
  komad: EquipmentPiece;
  artikl: EquipmentArticle;
  mjesto: EquipmentPlace;
  povijest: EquipmentEvent[];
}
export interface EquipmentResult {
  id: string;
  vrijeme: string;
  ponistivo_do: string;
  status: string;
  poslao?: string;
  primio?: string | null;
  ponisti_ids?: string[];
  dokument?: EquipmentDocument;
  bez_potpisa_razlog?: string | null;
  bez_fotke_razlog?: string | null;
}
export interface EquipmentUndoSummary {
  ukupno: number;
  ponisteno: string[];
  neuspjelo: { id: string; razlog: string }[];
}
export interface EquipmentTransferInput {
  iz_mjesta_id: string;
  u_mjesto_id: string;
  datum: string;
  stavke: {
    artikl_id: string;
    komad_id: string | null;
    najam_id: string | null;
    kolicina: number;
  }[];
}
export interface EquipmentReceiptInput {
  stavke: {
    id?: string;
    artikl_id: string;
    komad_id: string | null;
    najam_id: string | null;
    primljeno: number;
    razlika_razlog: string | null;
  }[];
}
export type EquipmentMovementKind =
  | "ostecenje"
  | "u_radionu"
  | "iz_radione"
  | "produljenje_roka"
  | "otpis"
  | "nestalo"
  | "pronadeno"
  | "pocetno";
export interface EquipmentMovementInput {
  opis?: string;
  vrsta: EquipmentMovementKind;
  artikl_id: string;
  komad_id: string | null;
  najam_id: string | null;
  kolicina: number;
  mjesto_id: string;
  u_mjesto_id?: string;
  razlog?: string;
  datum: string;
  rok_povratka?: string;
  ishod?: "popravljeno" | "ne_isplati_se";
  iznos_popravka?: string;
  zadnji_put_videno?: string;
  broj_prijave_policiji?: string;
  izvor_kretanje_id?: string;
  stanje?: "ispravno" | "osteceno" | "u_radioni";
  inventarni_broj?: string;
  serijski_broj?: string;
}
export interface EquipmentImportPreview {
  id: string;
  retci: {
    redak: number;
    artikl: string;
    mjesto: string;
    kolicina: number | null;
    inventarni_broj: string | null;
    greske: string[];
  }[];
}
export interface EquipmentMissing {
  kretanje_id: string;
  artikl_id: string;
  komad_id: string | null;
  mjesto_id: string;
  najam_id: string | null;
  kolicina_preostalo: number;
  datum: string;
}

// E1-B wire contract, verified against f274863. View models above keep screens stable.
export type WirePlace = Omit<EquipmentPlace, "inventura_istekla"> & {
  inventura_status?: "nije_popisano" | "isteklo" | "u_roku";
  zadnja_inventura?: string | null;
};
export interface WireDocument {
  id: string;
  broj: string;
  content_url: string;
  bez_potpisa: boolean;
  bez_fotke: boolean;
  potpisani: string[];
}
export interface WireMovement {
  id: string;
  vrsta: string;
  artikl_id: string;
  komad_id: string | null;
  kolicina: number;
  iz_mjesta_id: string | null;
  u_mjesto_id: string | null;
  najam_id: string | null;
  razlog: string | null;
  opis?: string | null;
  user_id: string;
  user_ime: string;
  izvrseno: boolean;
  odobrava_id: string | null;
  izvor_kretanje_id?: string | null;
  ponistava_id?: string | null;
  vrijeme: string;
  datum: string;
  status: "provedeno" | "ceka_upravu" | "odbijeno" | "ponisteno";
  dokumenti: string[];
  dokument: WireDocument | null;
  ponisti_do: string;
  bez_potpisa: boolean;
}
export interface WireTransfer extends Omit<
  EquipmentTransfer,
  "poslao" | "stavke" | "dokument"
> {
  poslao_user_id: string;
  poslao_ime: string;
  primio_ime: string | null;
  primio_user_id: string | null;
  primljeno_at: string | null;
  stavke: (EquipmentTransferItem & {
    id: string;
    selidba_id: string;
    stanje: string;
    razlika_rjesenje: string | null;
    rok_povratka: string | null;
  })[];
  kretanja: WireMovement[];
}
export interface WireOverview {
  artikli: {
    artikl_id: string;
    najam_id: string | null;
    ukupno: number;
    u_prijevozu: number;
    ceka_otpis: number;
    po_mjestu: {
      mjesto_id: string;
      ispravno: number;
      osteceno: number;
      u_radioni: number;
      ceka_otpis: number;
    }[];
    artikl: EquipmentArticle;
    najam: { broj_ugovora: string | null; povrat_do: string | null; najmodavac?: string | null } | null;
  }[];
  mjesta: WirePlace[];
  komadi: (Omit<EquipmentPiece, "najmodavac" | "povrat_do" | "fotka"> & {
    dokument_id?: string | null;
  })[];
  plocice: {
    u_prijevozu: number;
    ceka_potvrdu_uprave: number;
    u_radioni_preko_roka: number;
    mjesta_nije_popisano: number;
    inventura_istekla: number;
  };
  stize: WireTransfer[];
  trazi_se?: EquipmentShortage[];
  ceka_potvrdu: WireMovement[];
  povijest: WireMovement[];
  upozorenja: (
    | {
        kod: "NEPRIMLJENO_2_DANA" | "NEPRIMLJENO_5_DANA";
        selidba_id: string;
        boja: string;
      }
    | { kod: "RADIONA_PREKO_ROKA"; kolicina: number; boja: string }
    | { kod: "E2"; poruka: string; boja: string }
  )[];
}
export interface WireImportPreview {
  uvoz_id: string | null;
  redci: {
    redak: number;
    artikl: string;
    mjesto: string;
    kolicina: string;
    inventarni_broj: string;
  }[];
  greske: { redak: number; polje: string; poruka: string }[];
  broj_redaka: number;
  moze_potvrditi: boolean;
}
export type WirePieceDetail = WireOverview["komadi"][number] & {
  najmodavac?: string | null;
  povrat_do?: string | null;
  artikl: Pick<
    EquipmentArticle,
    "id" | "naziv" | "skupina" | "nacin" | "jedinica"
  >;
  mjesto: Pick<EquipmentPlace, "id" | "naziv" | "vrsta">;
  povijest: WireMovement[];
};
const root = "/oprema";
const reasonCodes: Record<string, string> = {
  Puknuto: "puknuto",
  Savijeno: "savijeno",
  "Ne radi": "ne_radi",
  "Nedostaje dio": "nedostaje_dio",
  Dotrajalo: "dotrajalo",
  Uništeno: "unisteno",
  "Ne isplati se popravak": "ne_isplati_se_popravak",
  Manjak: "manjak",
  "Oštećeno u prijevozu": "osteceno_u_prijevozu",
  "Krivi artikl": "krivi_artikl",
  "Krivo upisano ranije": "krivo_upisano_ranije",
};
const fieldNames: Record<string, string> = {
  dokumenti: "fotke",
  potpis_id: "potpis",
  opis: "opis",
  zadnji_put_vidjeno: "zadnji_put_videno",
  izvor_kretanje_id: "izvor_kretanje_id",
};
async function request<T>(
  method: Method,
  url: string,
  data?: unknown,
  signal?: AbortSignal,
  params?: object,
  blob = false,
): Promise<T> {
  try {
    return (
      await api.request<T>({
        method,
        url,
        data,
        signal,
        params,
        ...(blob ? { responseType: "blob" } : {}),
      })
    ).data;
  } catch (error) {
    if (isAxiosError(error) && error.response && !blob) {
      const detail = error.response.data?.detail;
      if (
        detail &&
        typeof detail === "object" &&
        !Array.isArray(detail) &&
        typeof detail.poruka === "string"
      ) {
        error.response.data = {
          ...error.response.data,
          detail: detail.polje
            ? [
                {
                  loc: ["body", fieldNames[detail.polje] ?? detail.polje],
                  msg: detail.poruka,
                },
              ]
            : detail.poruka,
        };
      } else if (Array.isArray(detail)) {
        error.response.data = {
          ...error.response.data,
          detail: detail.map((item) => ({
            ...item,
            loc: item.loc?.map((field: string | number) =>
              typeof field === "string" ? (fieldNames[field] ?? field) : field,
            ),
          })),
        };
      }
    }
    throw error;
  }
}
function multipart(
  data: object,
  photos: File[],
  signature: File | null = null,
  evidence: EquipmentEvidenceInput = {},
) {
  const body = new FormData();
  body.append("podaci", JSON.stringify({ ...data, ...evidence }));
  photos.forEach((file) => body.append("fotke", file));
  if (signature) body.append("potpis", signature);
  return body;
}
function documentView(
  document: WireDocument | null | undefined,
  movement: string,
): EquipmentDocument | undefined {
  return document
    ? { id: document.id, broj: document.broj, kretanje_id: movement, content_url: document.content_url }
    : undefined;
}
function movementView(row: WireMovement): EquipmentEvent {
  return {
    ...row,
    razlog:
      row.razlog === "drugo"
        ? (row.opis ?? "Drugo")
        : (Object.entries(reasonCodes).find(
            ([, code]) => code === row.razlog,
          )?.[0] ?? row.razlog),
    status: row.status === "ceka_upravu" ? "ceka_potvrdu" : row.status,
    korisnik: row.user_ime,
    fotke: row.dokumenti.map((id) => ({
      id,
      naziv: "Dokaz",
      url: `/api/oprema/datoteke/${id}`,
    })),
    dokument: documentView(row.dokument, row.id),
    bez_potpisa_razlog:
      row.bez_potpisa || row.dokument?.bez_potpisa
        ? "Potpis nije priložen"
        : null,
    bez_fotke_razlog: row.dokument?.bez_fotke ? "Fotka nije priložena" : null,
  };
}
function resultView(
  row: WireMovement,
  evidence: EquipmentEvidenceInput = {},
): EquipmentResult {
  const view = movementView(row);
  return {
    id: row.id,
    vrijeme: row.vrijeme,
    ponistivo_do: row.ponisti_do,
    status: view.status,
    dokument: view.dokument,
    bez_potpisa_razlog: view.bez_potpisa_razlog
      ? evidence.bez_potpisa_razlog || view.bez_potpisa_razlog
      : null,
    bez_fotke_razlog: view.bez_fotke_razlog
      ? evidence.bez_fotke_razlog || view.bez_fotke_razlog
      : null,
  };
}
function transferView(row: WireTransfer): EquipmentTransfer {
  return {
    ...row,
    poslao: row.poslao_ime,
    primio: row.primio_ime,
    dokument: documentView(
      row.kretanja[0]?.dokument,
      row.kretanja[0]?.id ?? "",
    ),
  };
}
function placeView(row: WirePlace): EquipmentPlace {
  return { ...row, inventura_istekla: row.inventura_status === "isteklo" };
}
function overviewView(
  raw: WireOverview,
  selectedPlace?: string,
): EquipmentOverview {
  const balances: EquipmentBalance[] = [];
  for (const group of raw.artikli.filter(
    (g) => g.artikl.nacin === "kolicina",
  )) {
    const owner = {
      najam_id: group.najam_id,
      najmodavac: group.najam
        ? group.najam.najmodavac || `Ugovor ${group.najam.broj_ugovora ?? "najma"}`
        : null,
      povrat_do: group.najam?.povrat_do ?? null,
    };
    group.po_mjestu.forEach((p) =>
      balances.push({
        ...p,
        ...owner,
        artikl_id: group.artikl_id,
        u_prijevozu: 0,
      }),
    );
    let assigned = 0;
    for (const transfer of raw.stize.filter(
      (t) => t.status === "u_prijevozu",
    )) {
      const quantity = transfer.stavke
        .filter(
          (s) =>
            s.artikl_id === group.artikl_id &&
            s.najam_id === group.najam_id &&
            !s.komad_id,
        )
        .reduce((n, s) => n + s.poslano, 0);
      if (!quantity) continue;
      let balance = balances.find(
        (s) =>
          s.artikl_id === group.artikl_id &&
          s.najam_id === group.najam_id &&
          s.mjesto_id === transfer.u_mjesto_id,
      );
      if (!balance) {
        balance = {
          ...owner,
          artikl_id: group.artikl_id,
          mjesto_id: transfer.u_mjesto_id,
          ispravno: 0,
          osteceno: 0,
          u_radioni: 0,
          u_prijevozu: 0,
        };
        balances.push(balance);
      }
      balance.u_prijevozu += quantity;
      assigned += quantity;
    }
    if (!selectedPlace && group.u_prijevozu > assigned)
      balances.push({
        ...owner,
        artikl_id: group.artikl_id,
        mjesto_id: "in-transit",
        ispravno: 0,
        osteceno: 0,
        u_radioni: 0,
        u_prijevozu: group.u_prijevozu - assigned,
      });
  }
  const pieces = raw.komadi.map((p) => {
    const rental = raw.artikli.find(
      (g) => g.artikl_id === p.artikl_id && g.najam_id === p.najam_id,
    )?.najam;
    return {
      ...p,
      najmodavac: rental ? rental.najmodavac || `Ugovor ${rental.broj_ugovora ?? "najma"}` : null,
      povrat_do: rental?.povrat_do ?? null,
      fotka: p.dokument_id ? `/api/oprema/datoteke/${p.dokument_id}` : null,
    };
  });
  // In-transit pieces are absent from Komad[]; represent them from the owner's overview bucket.
  if (!selectedPlace)
    for (const g of raw.artikli.filter(
      (g) => g.artikl.nacin === "komad" && g.u_prijevozu > 0,
    ))
      balances.push({
        artikl_id: g.artikl_id,
        mjesto_id: "in-transit",
        najam_id: g.najam_id,
        najmodavac: g.najam?.najmodavac ?? null,
        povrat_do: g.najam?.povrat_do ?? null,
        ispravno: 0,
        osteceno: 0,
        u_radioni: 0,
        u_prijevozu: g.u_prijevozu,
      });
  return {
    artikli: [
      ...new Map(raw.artikli.map((g) => [g.artikl.id, g.artikl])).values(),
    ],
    mjesta: raw.mjesta.map(placeView),
    stanje: balances,
    komadi: pieces,
    stize: raw.stize.map(transferView),
    plocice: {
      u_prijevozu: raw.plocice.u_prijevozu,
      ceka_potvrdu: raw.plocice.ceka_potvrdu_uprave,
      radiona_preko_roka: raw.plocice.u_radioni_preko_roka,
      nije_popisano: raw.plocice.mjesta_nije_popisano,
      inventura_istekla: raw.plocice.inventura_istekla,
      bez_potpisa: new Set(
        raw.povijest
          .filter((e) => e.bez_potpisa && e.dokument)
          .map((e) => e.dokument!.id),
      ).size,
    },
  };
}
function placeDetail(
  raw: WireOverview,
  id?: string,
  site?: string,
): EquipmentPlaceDetail {
  const view = overviewView(raw, id ?? site),
    place = view.mjesta.find((p) =>
      id ? p.id === id : p.gradiliste_id === site,
    );
  if (!place) throw new Error("Mjesto nije pronađeno u odgovoru API-ja.");
  return {
    mjesto: place,
    artikli: view.artikli,
    stanje: view.stanje.filter((s) => s.mjesto_id === place.id),
    komadi: view.komadi.filter((p) => p.mjesto_id === place.id),
    stize: raw.stize
      .filter((t) => t.u_mjesto_id === place.id)
      .map(transferView),
    trazi_se: (raw.trazi_se ?? []).filter(
      (row) => row.iz_mjesta_id === place.id,
    ),
    povijest: raw.povijest.map(movementView),
    upozorenja: [
      ...raw.upozorenja.filter((w) => w.kod !== "E2").map((w) =>
        w.kod === "RADIONA_PREKO_ROKA"
          ? `U radioni preko roka: ${w.kolicina} kom`
          : w.kod === "NEPRIMLJENO_5_DANA"
            ? "Primitak nije potvrđen 5 dana."
            : "Primitak nije potvrđen 2 dana.",
      ),
      ...raw.povijest
        .filter((e) => e.bez_potpisa)
        .map((e) => `Bez potpisa · ${e.dokument?.broj ?? "Dokument"}`),
    ],
  };
}
function transferResult(
  raw: WireTransfer,
  kind: string,
  evidence: EquipmentEvidenceInput,
) {
  const row = raw.kretanja.find((k) =>
    kind === "selidba_poslano"
      ? ["selidba_poslano", "zaduzenje", "povrat"].includes(k.vrsta)
      : k.vrsta === kind,
  );
  if (!row) throw new Error("API nije vratio zapis radnje.");
  return {
    ...resultView(row, evidence),
    poslao: raw.poslao_ime,
    primio: raw.primio_ime,
  };
}
export const siteEquipmentApi = {
  suppliers: (signal?: AbortSignal) =>
    request<{ id: string; naziv: string }[]>(
      "GET",
      `${root}/dobavljaci`,
      undefined,
      signal,
    ),
  resolveDifference: async (
    transferId: string,
    itemId: string,
    data: { rjesenje: "pronadeno" | "nestalo"; razlog: string; datum: string },
  ) =>
    resultView(
      await request<WireMovement>(
        "POST",
        `${root}/selidbe/${transferId}/stavke/${itemId}/rijesi-razliku`,
        data,
      ),
    ),
  overview: async (signal?: AbortSignal) =>
    overviewView(
      await request<WireOverview>("GET", `${root}/pregled`, undefined, signal),
    ),
  articles: (signal?: AbortSignal) =>
    request<EquipmentArticle[]>("GET", `${root}/artikli`, undefined, signal),
  saveArticle: (data: Omit<EquipmentArticle, "id">, id?: string) => {
    const payload = {
      naziv: data.naziv,
      skupina: data.skupina,
      nacin: data.nacin,
      jedinica: data.jedinica,
      m2_po_kom: data.m2_po_kom,
      nabavna_cijena: data.nabavna_cijena,
      aktivna: data.aktivna,
    };
    return request<EquipmentArticle>(
      id ? "PATCH" : "POST",
      `${root}/artikli${id ? `/${id}` : ""}`,
      payload,
    );
  },
  places: async (signal?: AbortSignal, destinations = false) => {
    const places = (
      await request<WirePlace[]>("GET", `${root}/mjesta`, undefined, signal)
    ).map(placeView);
    if (!destinations) return places;
    const extra = await request<Pick<WirePlace, "id" | "naziv" | "vrsta">[]>(
      "GET",
      `${root}/mjesta`,
      undefined,
      signal,
      { odredista: true },
    );
    return [
      ...new Map(
        [
          ...extra.map((p) => ({
            ...p,
            gradiliste_id: null,
            aktivno: true,
            popisano_at: null,
            inventura_istekla: false,
          })),
          ...places,
        ].map((p) => [p.id, p]),
      ).values(),
    ];
  },
  createPlace: (data: {
    naziv: string;
    vrsta: "baza" | "radiona";
    dobavljac_id: string | null;
  }) => request<WirePlace>("POST", `${root}/mjesta`, data),
  patchPlace: (id: string, data: { naziv?: string; aktivno?: boolean }) =>
    request<WirePlace>("PATCH", `${root}/mjesta/${id}`, data),
  place: async (id: string, signal?: AbortSignal) =>
    placeDetail(
      await request<WireOverview>(
        "GET",
        `${root}/mjesta/${id}`,
        undefined,
        signal,
      ),
      id,
    ),
  site: async (id: string, signal?: AbortSignal) =>
    placeDetail(
      await request<WireOverview>(
        "GET",
        `/gradilista/${id}/oprema`,
        undefined,
        signal,
      ),
      undefined,
      id,
    ),
  piece: async (
    id: string,
    signal?: AbortSignal,
  ): Promise<EquipmentPieceDetail> => {
    const raw = await request<WirePieceDetail>(
      "GET",
      `${root}/komadi/${id}`,
      undefined,
      signal,
    );
    return {
      komad: {
        ...raw,
        ceka_otpis:
          raw.ceka_otpis ??
          raw.povijest.some(
            (e) =>
              e.vrsta === "otpis" &&
              e.status === "ceka_upravu" &&
              !raw.povijest.some(
                (decision) =>
                  decision.odobrava_id === e.id &&
                  decision.status !== "ponisteno",
              ),
          ),
        najmodavac: raw.najmodavac ?? null,
        povrat_do: raw.povrat_do ?? null,
        fotka: raw.dokument_id
          ? `/api/oprema/datoteke/${raw.dokument_id}`
          : null,
      },
      artikl: {
        ...raw.artikl,
        aktivna: true,
        m2_po_kom: null,
        nabavna_cijena: null,
      },
      mjesto: {
        ...raw.mjesto,
        gradiliste_id: null,
        aktivno: true,
        popisano_at: null,
        inventura_istekla: false,
      },
      povijest: raw.povijest.map(movementView),
    };
  },
  missing: (signal?: AbortSignal) =>
    request<EquipmentMissing[]>("GET", `${root}/nestalo`, undefined, signal),
  history: async (id: string, place?: string, signal?: AbortSignal) =>
    (
      await request<WireMovement[]>(
        "GET",
        `${root}/artikli/${id}/povijest`,
        undefined,
        signal,
        { mjesto: place },
      )
    ).map(movementView),
  transfer: async (
    data: EquipmentTransferInput,
    photos: File[],
    signature: File | null = null,
    evidence: EquipmentEvidenceInput = {},
  ) =>
    transferResult(
      await request<WireTransfer>(
        "POST",
        `${root}/selidbe`,
        multipart(data, photos, signature, evidence),
      ),
      "selidba_poslano",
      evidence,
    ),
  receive: async (
    id: string,
    data: EquipmentReceiptInput,
    photos: File[],
    signature: File | null = null,
    evidence: EquipmentEvidenceInput = {},
  ) => {
    const payload = {
      stavke: data.stavke.map((s) => {
        if (!s.id) throw new Error("Nedostaje oznaka stavke primitka.");
        return {
          id: s.id,
          primljeno: s.primljeno,
          razlika_razlog: s.razlika_razlog
            ? (reasonCodes[s.razlika_razlog] ?? s.razlika_razlog)
            : null,
        };
      }),
    };
    return transferResult(
      await request<WireTransfer>(
        "POST",
        `${root}/selidbe/${id}/primitak`,
        multipart(payload, photos, signature, evidence),
      ),
      "selidba_primljeno",
      evidence,
    );
  },
  move: async (
    data: EquipmentMovementInput,
    photos: File[],
    signature: File | null = null,
    evidence: EquipmentEvidenceInput = {},
  ) => {
    const { zadnji_put_videno, ...payload } = data;
    const reason = data.razlog?.startsWith("Drugo:")
      ? "drugo"
      : (reasonCodes[data.razlog ?? ""] ?? data.razlog);
    return resultView(
      await request<WireMovement>(
        "POST",
        `${root}/kretanja`,
        multipart(
          {
            ...payload,
            razlog: reason,
            ...(reason === "drugo"
              ? {
                  opis: data.razlog?.startsWith("Drugo:")
                    ? data.razlog.slice(6).trim()
                    : data.opis,
                }
              : {}),
            ...(zadnji_put_videno
              ? { zadnji_put_vidjeno: zadnji_put_videno }
              : {}),
            ...(data.vrsta === "pronadeno"
              ? {
                  mjesto_id: data.u_mjesto_id ?? data.mjesto_id,
                  u_mjesto_id: null,
                }
              : {}),
          },
          photos,
          signature,
          evidence,
        ),
      ),
      evidence,
    );
  },
  undo: async (id: string, group?: string[]) => {
    if (!group?.length) {
      await request("POST", `${root}/kretanja/${id}/ponisti`);
      return;
    }
    const ids = [...new Set(group)];
    const summary: EquipmentUndoSummary = {
      ukupno: ids.length,
      ponisteno: [],
      neuspjelo: [],
    };
    for (let offset = 0; offset < ids.length; offset += 10) {
      const batch = ids.slice(offset, offset + 10);
      const results = await Promise.allSettled(
        batch.map((current) =>
          request("POST", `${root}/kretanja/${current}/ponisti`),
        ),
      );
      results.forEach((result, index) => {
        const current = batch[index]!;
        if (result.status === "fulfilled") summary.ponisteno.push(current);
        else
          summary.neuspjelo.push({
            id: current,
            razlog: errorMessage(result.reason, "Poništenje nije uspjelo."),
          });
      });
    }
    return summary;
  },
  document: (id: string, signal?: AbortSignal, contentUrl?: string) =>
    request<Blob>(
      "GET",
      contentUrl?.startsWith("/api/oprema/e2/dokumenti/") ? `${root}/e2/dokumenti/${id}` : `${root}/dokumenti/${id}`,
      undefined,
      signal,
      undefined,
      true,
    ),
  photo: (id: string, signal?: AbortSignal) =>
    request<Blob>(
      "GET",
      `${root}/datoteke/${id}`,
      undefined,
      signal,
      undefined,
      true,
    ),
  attachSigned: async (id: string, file: File) => {
    await request(
      "POST",
      `${root}/kretanja/${id}/potpisani`,
      multipart({}, [file]),
    );
  },
  approvals: async (signal?: AbortSignal) =>
    (
      await request<WireOverview>("GET", `${root}/pregled`, undefined, signal)
    ).ceka_potvrdu.map(movementView),
  approve: async (id: string, accept: boolean, razlog: string) =>
    resultView(
      await request<WireMovement>(
        "POST",
        `${root}/kretanja/${id}/${accept ? "odobri" : "odbij"}`,
        { razlog },
      ),
    ),
  template: () =>
    request<Blob>(
      "GET",
      `${root}/uvoz/predlozak`,
      undefined,
      undefined,
      undefined,
      true,
    ),
  preview: async (file: File): Promise<EquipmentImportPreview> => {
    const body = new FormData();
    body.append("datoteka", file);
    const raw = await request<WireImportPreview>("POST", `${root}/uvoz`, body);
    return {
      id: raw.moze_potvrditi ? (raw.uvoz_id ?? "") : "",
      retci: raw.redci.map((r) => ({
        redak: r.redak,
        artikl: r.artikl,
        mjesto: r.mjesto,
        kolicina: r.kolicina ? Number(r.kolicina) : null,
        inventarni_broj: r.inventarni_broj || null,
        greske: raw.greske
          .filter((e) => e.redak === r.redak)
          .map((e) => e.poruka),
      })),
    };
  },
  import: async (id: string): Promise<EquipmentResult> => {
    const raw = await request<{
      uvoz_id: string;
      uvezeno: number;
      kretanja: string[];
      ponisti_do: string;
    }>("POST", `${root}/uvoz/potvrdi`, { uvoz_id: id });
    const first = raw.kretanja[0];
    if (!first) throw new Error("API nije vratio zapise uvoza.");
    return {
      id: first,
      vrijeme: new Date(
        new Date(raw.ponisti_do).getTime() - 120_000,
      ).toISOString(),
      status: "provedeno",
      ponistivo_do: raw.ponisti_do,
      ponisti_ids: raw.kretanja,
    };
  },
};
